import { BehaviorSubject, combineLatest, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ContentService } from '@core/services/content.service';
import { SearchService } from '@core/services/search.service';
import { SiteStateService } from '@core/services/site-state/site-state.service';
import { customFilterParams } from '@features/product-filter/product-filter.config';
import { ProductSearchViewModelGenericSearchResultViewModel as ProductSearchResult } from '@models/api-search.models';
import { SearchResultCmsPageViewModel } from '@models/api.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { setLoadingState } from './operators/set-loading-state.operator';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { ScrollLockService } from '@core/services/scroll/scroll-lock.service';
import { RelewiseRetailMediaParams } from '@core/services/relewise/relewise-tracking.model';
import { processRetailMediaProducts } from '@core/services/relewise/process-retail-media.helper';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class SearchStateService {
    private searchTermBs$ = new BehaviorSubject<string>('');
    private productResultSearchStringRs$ = new ReplaySubject<string>();

    private relewiseRetailMediaParams?: RelewiseRetailMediaParams = {
        retailMediaPlacements: 'TOP',
        retailMediaVariationKey: 'MOBILE'
    };

    private productSearchResultsRs$ = new ReplaySubject<ProductSearchResult | undefined | null>();
    private contentSearchResultsRs$ = new ReplaySubject<SearchResultCmsPageViewModel[] | undefined | null>();
    private productSearchInProgressBs$ = new BehaviorSubject<boolean>(false);
    private contentSearchInProgressBs$ = new BehaviorSubject<boolean>(false);

    public productSearchResults$ = this.productSearchResultsRs$.asObservable();
    public contentSearchResults$ = this.contentSearchResultsRs$.asObservable();
    public productSearchInProgress$ = this.productSearchInProgressBs$.asObservable();
    public contentSearchInProgress$ = this.contentSearchInProgressBs$.asObservable();

    public searchTerm$ = this.searchTermBs$.asObservable();
    public productResultSearchString$ = this.productResultSearchStringRs$.asObservable();

    constructor(
        private readonly searchService: SearchService,
        private readonly contentService: ContentService,
        private readonly siteStateService: SiteStateService,
        private readonly scrollLockService: ScrollLockService,
        private readonly router: Router,
        private readonly relewiseTrackingService: RelewiseTrackingService
    ) {
        // Close overlay when navigating
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                untilDestroyed(this)
            )
            .subscribe(() => this.close());

        // Product search
        this.searchTermBs$
            .pipe(
                setLoadingState(this.productSearchInProgressBs$),
                distinctUntilChanged(),
                switchMap(searchString =>
                    combineLatest([
                        of(searchString),
                        this.searchService.getFilter({
                            [customFilterParams.searchQuery]: searchString,
                            take: 3,
                            ...this.relewiseRetailMediaParams,
                            ...this.relewiseTrackingService.getRelewiseSearchParams()
                        })
                    ])
                ),
                tap(([, { temporaryId }]) => this.relewiseTrackingService.updateTempId(temporaryId)),
                catchError(error => {
                    this.productSearchInProgressBs$.next(false);
                    return throwError(error);
                }),
                untilDestroyed(this)
            )
            .subscribe(([searchString, results]) => {
                this.productResultSearchStringRs$.next(searchString);
                this.productSearchResultsRs$.next(processRetailMediaProducts(results));
                this.productSearchInProgressBs$.next(false);
            });

        // Content search
        this.searchTermBs$
            .pipe(
                setLoadingState(this.contentSearchInProgressBs$),
                untilDestroyed(this),
                switchMap(searchString => this.contentService.getContentSearch(searchString, { params: { take: 3 } })),
                catchError(error => {
                    this.contentSearchInProgressBs$.next(false);
                    return throwError(error);
                })
            )
            .subscribe(results => {
                this.contentSearchResultsRs$.next(results);
                this.contentSearchInProgressBs$.next(false);
            });

        // Site state
        this.searchTermBs$.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe(value => {
            const searchActive = value.length > 0;
            this.siteStateService.setMultipleStates({ search: searchActive, mobileMenu: false });
            this.scrollLockService.lockScroll$.next(searchActive);

            // Reset results, to clear possible stale data
            if (!searchActive) {
                this.productSearchResultsRs$.next(null);
                this.contentSearchResultsRs$.next(null);
            }
        });
    }

    public setSearchRelwiseRetailMediaParams(params: RelewiseRetailMediaParams): void {
        this.relewiseRetailMediaParams = params;
    }

    public setSearchResult(productSearchResults?: ProductSearchResult): void {
        this.productSearchResultsRs$.next(productSearchResults);
    }

    public setContentSearchResult(contentSearchResults?: SearchResultCmsPageViewModel[]): void {
        this.contentSearchResultsRs$.next(contentSearchResults);
    }

    public setProductSearchInProgress(inProgress: boolean): void {
        this.productSearchInProgressBs$.next(inProgress);
    }

    public setSearchTerm(searchTerm: string): void {
        this.searchTermBs$.next(searchTerm);
    }

    public close(): void {
        this.contentSearchInProgressBs$.next(false);
        this.productSearchInProgressBs$.next(false);
        this.siteStateService.setState('search', false);
        this.scrollLockService.lockScroll$.next(false);
    }
}
